import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import {
  useBreakpoint,
  Breakpoints,
  useNextgenRouter,
  useSession,
  Tabs,
  TabDesign,
  useTranslation,
} from '@nextgen-web-framework/all';
import { ErrorCard, Errors } from '@commerce-webcomponents/ui';
import {
  BucketField,
  ContentCategory,
  ContentSearchResultEntry,
  Product,
  ProductSearch,
  filterInitialState,
  transformContentCardSearchResults,
  SRP_TABS,
} from '@commerce-search-webcomponents/sdk';
import { Logger, PreferenceService, StorageService } from '@nextgen-web-framework/core';
import { productDataTransformer, ProductData, categoryApi } from '@commerce-product-webcomponents/sdk';
import { ContentCardResults, ModeEnum } from '@commerce-search-webcomponents/ui';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import withSearchResultProps from '../../hoc/withSearchResultProps';
import { contentSearchSite } from '../SearchOverlay/types/constants';
import {
  CachedCategoryAndSolutionData,
  CategoryAndSolutionDetails,
  ContentQueryRef,
  QueryRef,
  SearchResultComponentProps,
} from './types/propTypes';
import { LIMIT } from './types/enums';
import Banner from './Banner/Banner';
import {
  buildCategoryHierarchy,
  CAT_AND_SOL_STORAGE_KEY,
  fetchCategoriesAndSolutions,
  findCategoryOrSolutionFacetDetails,
} from './SearchResultUtils';
import ProductResults from './ProductResults/ProductResults';
import styles from './SearchResult.module.scss';

const UPPER_LIMIT_SUGGESTIONS = 5;
const { CATEGORY, SOLUTION } = BucketField;
const { XS, SM, MD, LG, XL } = Breakpoints;
const CONTENT_SEARCH_PAGE_SIZE = 6;

const SearchResult = ({
  goToShop,
  searchedKeyword,
  originallySearchedText,
  isPLP,
  plpType,
  offset,
  limit,
  isLoading,
  contentSearchLoaders,
  fulltextSearchError,
  fulltextSearchResults,
  articleResults,
  offersResults,
  facets,
  filter,
  availableQuickFilters,
  quickFilter,
  noResultFilterName,
  updateLimit,
  updateOffset,
  resetSearchResult,
  fetchResults,
  updateFilter,
  updateSearchTab,
  updateQuickFilter,
  resetFilter,
  fetchContentResult,
}: SearchResultComponentProps) => {
  const [productsData, setProductsData] = useState<Array<Product>>([]);
  const [articleData, setArticleData] = useState<Array<ContentSearchResultEntry>>([]);
  const [offerData, setOfferData] = useState<Array<ContentSearchResultEntry>>([]);
  const [lastProductCard, setLastProductCard] = useState<HTMLElement | null>();
  const [articleQuickFilters, setArticleQuickFilters] = useState<string[]>([]);
  const [offerQuickFilters, setOfferQuickFilters] = useState<string[]>([]);
  const [lastArticleTile, setLastArticleTile] = useState<HTMLElement | null>();
  const [lastOfferTile, setLastOfferTile] = useState<HTMLElement | null>();
  const [showDesktopFacets, setShowDesktopFacets] = useState<boolean>(false);
  const [priceRangeInterval, setPriceRangeInterval] = useState<number>();
  const [badgeCount, setBadgeCount] = useState<number>(0);
  const [searchedTextHasData, setSearchedTextHasData] = useState<boolean>(false);
  const [categoryAndSolutionDetails, setCategoryAndSolutionDetails] = useState<CategoryAndSolutionDetails>();
  const [masterCategoryAndSolutionData, setMasterCategoryAndSolutionData] = useState<CategoryAndSolutionDetails>();
  const [selectedTab, setSelectedTab] = useState<SRP_TABS>(SRP_TABS.PRODUCTS);
  const uniqueProductIds = useRef<Set<string>>(new Set([]));
  const resultContainerRef = useRef<HTMLDivElement>(null);
  const queryRef = useRef<QueryRef>();
  queryRef.current = {
    offset,
    limit,
    fulltextSearchResults,
    searchedKeyword,
    filter,
    fetchResults,
  }; // To use latest state values in callback
  const viewPortType = useBreakpoint() as Breakpoints;
  const router = useNextgenRouter();
  const {
    session: { warehouseId },
  } = useSession();
  const t = useTranslation();
  const { locale } = PreferenceService.getPreferences();
  const logger = Logger.getInstance({ component: 'SearchResult', product: 'Search' });
  const sessionStorage = StorageService.getSessionStorage();
  const containerWidth = resultContainerRef.current?.getBoundingClientRect().width;
  const { isOffersLoading = false, isArticlesLoading = false } = contentSearchLoaders || {};
  const {
    search_results: offerSearchResults = [],
    sub_category_lists: subCategoryListsOffer = [],
    subCategoryFilterName: subCategoryFilterNameOffer = '',
    total_size: totalCountOffer = 0,
    next_page_token: nextPageTokenOffer = '',
  } = offersResults || {};
  const {
    search_results: articleSearchResults = [],
    sub_category_lists: subCategoryListsArticle = [],
    subCategoryFilterName: subCategoryFilterNameArticle = '',
    total_size: totalCountArticle = 0,
    next_page_token: nextPageTokenArticle = '',
  } = articleResults || {};
  const articleQuery = useRef<ContentQueryRef>();
  articleQuery.current = {
    nextPageToken: nextPageTokenArticle,
    subCategoryFilterName: subCategoryFilterNameArticle,
    limit,
    searchedKeyword,
    fetchContentResult,
  };
  const offerQuery = useRef<ContentQueryRef>();
  offerQuery.current = {
    nextPageToken: nextPageTokenOffer,
    subCategoryFilterName: subCategoryFilterNameOffer,
    limit,
    searchedKeyword,
    fetchContentResult,
  };

  const { NEXT_PUBLIC_ENABLE_CONTENT_SEARCH: isContentSearchEnabled } = ConfigService.getConfigs() as {
    NEXT_PUBLIC_ENABLE_CONTENT_SEARCH?: boolean;
  };

  const tabList = [
    { title: t('search.common.products'), id: SRP_TABS.PRODUCTS },
    { title: t('search.searchResult.offers'), id: SRP_TABS.OFFERS, disabled: !offerSearchResults?.length },
    {
      title: t('search.searchResult.articles'),
      id: SRP_TABS.ARTICLES,
      disabled: !articleSearchResults?.length,
    },
  ];

  const selectedTabIndex = useMemo(() => tabList.findIndex(({ id }) => id === selectedTab), [selectedTab]);

  useIntersectionObserver(lastProductCard, { rootMargin: '1px' }, () => {
    const isMoreResultsAvailable =
      Number(queryRef.current?.offset) < Number((queryRef.current?.fulltextSearchResults as ProductSearch)?.total);
    if (isMoreResultsAvailable) {
      queryRef.current?.fetchResults(queryRef.current?.searchedKeyword as string);
    }
  });

  useIntersectionObserver(lastArticleTile, { rootMargin: '1px' }, () => {
    const { nextPageToken = '', subCategoryFilterName } = articleQuery.current || {};
    if (nextPageToken) {
      articleQuery.current?.fetchContentResult({
        category: ContentCategory.Article,
        sub_category: subCategoryFilterName,
        query: articleQuery.current?.searchedKeyword,
        pageSize: CONTENT_SEARCH_PAGE_SIZE,
        next_page_token: nextPageToken,
        market: contentSearchSite,
        locale,
      });
    }
  });

  useIntersectionObserver(lastOfferTile, { rootMargin: '1px' }, () => {
    const { nextPageToken = '', subCategoryFilterName } = offerQuery.current || {};
    if (nextPageToken) {
      offerQuery.current?.fetchContentResult({
        category: ContentCategory.Offers,
        sub_category: subCategoryFilterName,
        query: offerQuery.current?.searchedKeyword,
        pageSize: CONTENT_SEARCH_PAGE_SIZE,
        next_page_token: nextPageToken,
        market: contentSearchSite,
        locale,
      });
    }
  });

  // cleanup on Component destroy
  useEffect(
    () => () => {
      resetSearchResult();
    },
    [],
  );

  useEffect(() => {
    fetchCategoriesAndSolutions(setMasterCategoryAndSolutionData, fetchCategoryOrSolutionData, isPLP);
  }, [router.params?.locale]);

  useEffect(() => {
    getCategoryOrSolutionFacetDetails(CATEGORY);
    getCategoryOrSolutionFacetDetails(SOLUTION);
  }, [facets, masterCategoryAndSolutionData]);

  useEffect(() => {
    const { values } = facets.find(({ field }) => field == BucketField.PRICE) || {};
    if (!priceRangeInterval && values && values.length > 1) {
      const interval = Number(values[1].name) - Number(values[0].name);
      if (!isNaN(interval)) {
        setPriceRangeInterval(interval);
      }
    }
  }, [facets]);

  useEffect(() => {
    const { total, offset, products } = fulltextSearchResults;
    if (!searchedTextHasData && total) {
      // searchedTextHasData is true if results found for searchKeyword (without filters applied)
      setSearchedTextHasData(true);
    }
    updateProductsData(products, offset);
    cacheSearchedKeyword(total);
  }, [fulltextSearchResults]);

  useLayoutEffect(() => {
    switch (viewPortType) {
      case MD:
        updateLimit(LIMIT.MOBILE_TABLET);
        break;
      case XS:
      case SM:
        updateLimit(LIMIT.MOBILE_TABLET);
        break;
      case LG:
      case XL:
      default: {
        if (showDesktopFacets) {
          updateLimit(LIMIT.DESKTOP_SIDENAV);
        } else {
          updateLimit(LIMIT.DESKTOP_NO_SIDENAV);
        }
      }
    }
  }, [containerWidth, viewPortType, showDesktopFacets]);

  useEffect(() => {
    updateOffset(0);
    uniqueProductIds.current.clear();
    if (filter !== filterInitialState) {
      fetchResults(searchedKeyword);
      scrollToTop();
    }
    let count = 0;
    Object.values(filter).forEach((value) => {
      count += Array.isArray(value) ? value.length : value ? 1 : 0;
    });
    setBadgeCount(count);
  }, [filter]);

  useEffect(() => {
    updateOffset(0);
    uniqueProductIds.current.clear();
    if (availableQuickFilters.length) {
      fetchResults(searchedKeyword);
      scrollToTop();
    }
  }, [quickFilter]);

  useEffect(() => {
    resetSearchResult();
  }, [searchedKeyword]);

  useEffect(() => {
    updateOffset(0);
    uniqueProductIds.current.clear();
    setSearchedTextHasData(false);
    fetchResults(searchedKeyword);
  }, [searchedKeyword, router.params?.locale, warehouseId]);

  useEffect(() => {
    if (!isPLP && isContentSearchEnabled) {
      setSelectedTab(SRP_TABS.PRODUCTS);
      setOfferData([]);
      setArticleData([]);
      setOfferQuickFilters([]);
      setArticleQuickFilters([]);
      fetchContentResult({
        category: ContentCategory.Offers,
        query: searchedKeyword,
        pageSize: CONTENT_SEARCH_PAGE_SIZE,
        next_page_token: '',
        market: contentSearchSite,
        locale,
      });
      fetchContentResult({
        category: ContentCategory.Article,
        query: searchedKeyword,
        pageSize: CONTENT_SEARCH_PAGE_SIZE,
        next_page_token: '',
        market: contentSearchSite,
        locale,
      });
    }
  }, [searchedKeyword, router.params?.locale]);

  useEffect(() => {
    if (selectedTab && !isPLP && isContentSearchEnabled) {
      updateSearchTab(selectedTab);
    }
  }, [selectedTab]);

  useEffect(() => {
    if (!isPLP && offerSearchResults && offerSearchResults.length > 0) {
      setOfferData((currentOffer) => [...currentOffer, ...offerSearchResults]);
      setOfferQuickFilters((currentQuickFilter) => [...new Set([...currentQuickFilter, ...subCategoryListsOffer])]);
    }
  }, [offerSearchResults]);

  useEffect(() => {
    if (!isPLP && articleSearchResults && articleSearchResults.length > 0) {
      setArticleData((currentArticles) => [...currentArticles, ...articleSearchResults]);
      setArticleQuickFilters((currentQuickFilter) => [...new Set([...currentQuickFilter, ...subCategoryListsArticle])]);
    }
  }, [articleSearchResults]);

  const contentSearchQuickFilterOffers = (name: string) => {
    setOfferData([]);
    fetchContentResult({
      category: ContentCategory.Offers,
      query: searchedKeyword,
      pageSize: CONTENT_SEARCH_PAGE_SIZE,
      next_page_token: '',
      market: contentSearchSite,
      sub_category: name,
      locale,
    });
  };

  const contentSearchQuickFilterArticles = (name: string) => {
    setArticleData([]);
    fetchContentResult({
      category: ContentCategory.Article,
      query: searchedKeyword,
      pageSize: CONTENT_SEARCH_PAGE_SIZE,
      next_page_token: '',
      market: contentSearchSite,
      sub_category: name,
      locale,
    });
  };

  const fetchCategoryOrSolutionData = async (field: BucketField.CATEGORY | BucketField.SOLUTION) => {
    try {
      const res = await categoryApi.getCategories({
        projections: 'CAT',
        treeId: field == CATEGORY ? 'CAT' : 'SOL',
      });
      setMasterCategoryAndSolutionData((prev) => ({ ...prev, [field]: buildCategoryHierarchy(res.categories, isPLP) }));
      const cacheData = (sessionStorage?.retrieve(CAT_AND_SOL_STORAGE_KEY) || {}) as CachedCategoryAndSolutionData;
      cacheData[locale] = { ...cacheData[locale], [field]: res.categories };
      sessionStorage.store(CAT_AND_SOL_STORAGE_KEY, cacheData);
    } catch (e) {
      logger.error(e);
    }
  };

  const getCategoryOrSolutionFacetDetails = (bucketField: BucketField.CATEGORY | BucketField.SOLUTION) => {
    const data = findCategoryOrSolutionFacetDetails({
      bucketField,
      facets,
      searchedKeyword,
      isPLP,
      masterCategoryAndSolutionData,
    });
    setCategoryAndSolutionDetails((prev) => ({ ...prev, [bucketField]: data }));
  };

  const updateProductsData = (products: Product[] | undefined, offset: number | undefined) => {
    if (Number(offset) === 0) {
      uniqueProductIds.current.clear();
    }
    const uniqueProducts: Product[] = [];
    products?.forEach((product) => {
      const { productId } = productDataTransformer(product as ProductData);
      if (!uniqueProductIds.current.has(productId)) {
        uniqueProductIds.current.add(productId);
        uniqueProducts.push(product);
      }
    });
    setProductsData((prevState) => ((offset as number) > 0 ? [...prevState, ...uniqueProducts] : [...uniqueProducts]));
  };

  const cacheSearchedKeyword = (total: number | undefined) => {
    // searchKeyword will be added to Recent Search for SRP (not PLP) if it has results
    if (!isPLP && searchedKeyword && total) {
      const recentSearches = (StorageService.getLocalStorage()?.retrieve('recentSearches') || []) as string[];
      if (!recentSearches?.includes(searchedKeyword)) {
        recentSearches.unshift(searchedKeyword);
        StorageService.getLocalStorage()?.store(
          'recentSearches',
          JSON.stringify(recentSearches.slice(0, UPPER_LIMIT_SUGGESTIONS)),
        );
      }
    }
  };

  const scrollToTop = () => {
    if ([XS, SM, MD].includes(viewPortType)) {
      window.scrollTo({ top: 0 });
    }
  };

  // Show error page if API fails, except while scrolling
  if (fulltextSearchError && offset == 0) {
    return <ErrorCard type={Errors.General} className="error-card" onActionClick={() => router.push('/')} />;
  }

  return (
    <div ref={resultContainerRef} data-testid="search-result-container" className={styles['search-result']}>
      {/* for PLP, Banner will come from Product and Tabs will be hidden */}
      {!isPLP && (
        <>
          <Banner
            searchedKeyword={searchedKeyword}
            originallySearchedText={originallySearchedText}
            hasSearchResult={Object.keys(fulltextSearchResults).length === 0 || !!fulltextSearchResults?.total}
          />
          {isContentSearchEnabled && (
            <Tabs
              className={styles['tabs-container']}
              tabList={tabList}
              design={TabDesign.TRANSPARENT}
              selectedTab={selectedTabIndex}
              onTabClick={(index) => setSelectedTab(tabList[index].id)}
            />
          )}
        </>
      )}
      {selectedTab === SRP_TABS.PRODUCTS && (
        <ProductResults
          searchedKeyword={searchedKeyword}
          isPLP={isPLP}
          plpType={plpType}
          noResultFilterName={noResultFilterName}
          limit={limit}
          isLoading={isLoading}
          fulltextSearchResults={fulltextSearchResults}
          facets={facets}
          filter={filter}
          quickFilter={quickFilter}
          availableQuickFilters={availableQuickFilters}
          productsData={productsData}
          searchedTextHasData={searchedTextHasData}
          badgeCount={badgeCount}
          priceRangeInterval={priceRangeInterval}
          categoryAndSolutionDetails={categoryAndSolutionDetails}
          showDesktopFacets={showDesktopFacets}
          goToShop={goToShop}
          setLastProductCard={setLastProductCard}
          setShowDesktopFacets={setShowDesktopFacets}
          updateFilter={updateFilter}
          updateQuickFilter={updateQuickFilter}
          resetFilter={resetFilter}
        />
      )}
      {selectedTab === SRP_TABS.OFFERS && (
        <ContentCardResults
          className={styles['content-result']}
          sectionTitle={`${t('search.searchResult.offerResults')} ${offerData.length ? `(${totalCountOffer})` : ''}`}
          cards={transformContentCardSearchResults(offerData)}
          loading={isOffersLoading}
          loadingItems={CONTENT_SEARCH_PAGE_SIZE}
          contentSearchQuickFiters={offerQuickFilters}
          selectedQuickFilter={subCategoryFilterNameOffer}
          mode={ModeEnum.OFFER}
          setLastCard={setLastOfferTile}
          updateQuickFilter={contentSearchQuickFilterOffers}
        />
      )}
      {selectedTab === SRP_TABS.ARTICLES && (
        <ContentCardResults
          className={styles['content-result']}
          sectionTitle={`${t('search.searchResult.articleResults')} ${articleData.length ? `(${totalCountArticle})` : ''}`}
          cards={transformContentCardSearchResults(articleData)}
          loading={isArticlesLoading}
          loadingItems={CONTENT_SEARCH_PAGE_SIZE}
          contentSearchQuickFiters={articleQuickFilters}
          selectedQuickFilter={subCategoryFilterNameArticle}
          mode={ModeEnum.ARTICLE}
          setLastCard={setLastArticleTile}
          updateQuickFilter={contentSearchQuickFilterArticles}
        />
      )}
    </div>
  );
};

export default withSearchResultProps(SearchResult);
